import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getAdminNotices,
  getAdminNotice,
  createNotice,
  updateNotice,
  deleteNotice,
  NoticeFilters,
} from '../api/notices.api';
import { Notice } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import DeleteNoticeModal from '../components/DeleteNoticeModal';

const AdminNoticesPage: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [isCurrentFilter, setIsCurrentFilter] = useState<boolean | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; noticeId: string | null; noticeTitle: string }>({
    show: false,
    noticeId: null,
    noticeTitle: '',
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    isCurrent: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [isFilterChanging, setIsFilterChanging] = useState(false);

  const fetchNotices = useCallback(async () => {
    setLoading(true);
    try {
      const filters: NoticeFilters = {
        page: currentPage,
        limit: 10,
        isCurrent: isCurrentFilter,
      };
      console.log('fetchNotices called with filters:', filters);
      const response = await getAdminNotices(filters);
      console.log('AdminNoticesPage - Response:', response);
      console.log('AdminNoticesPage - Response.data:', response.data);
      
      if (response && response.data) {
        if (response.data.notices) {
          let filteredNotices = Array.isArray(response.data.notices) ? response.data.notices : [];
          
          // Apply client-side filtering if filter is set
          if (isCurrentFilter !== null) {
            filteredNotices = filteredNotices.filter(notice => notice.isCurrent === isCurrentFilter);
            console.log('fetchNotices - Applied client-side filter. Filtered count:', filteredNotices.length, 'from', response.data.notices.length);
          }
          
          setNotices(filteredNotices);
          setTotalPages(Math.ceil(filteredNotices.length / 10) || 1);
          setTotal(filteredNotices.length);
        } else {
          console.warn('Response data structure unexpected:', response.data);
          setNotices([]);
          setTotalPages(1);
          setTotal(0);
        }
      } else {
        console.warn('No response data received');
        setNotices([]);
        setTotalPages(1);
        setTotal(0);
      }
    } catch (err: any) {
      console.error('Error fetching notices:', err);
      console.error('Error details:', err.response?.data);
      setNotices([]);
      setTotalPages(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, isCurrentFilter]);

  useEffect(() => {
    // Skip useEffect if filter is being changed manually
    if (isFilterChanging) {
      console.log('useEffect skipped - filter is being changed manually');
      return;
    }
    console.log('useEffect triggered - currentPage:', currentPage, 'isCurrentFilter:', isCurrentFilter);
    fetchNotices();
  }, [currentPage, isCurrentFilter, fetchNotices, isFilterChanging]);

  const handleOpenCreateModal = () => {
    const now = new Date();
    const dateString = now.toISOString().split('T')[0]; // YYYY-MM-DD format
    setFormData({
      title: '',
      description: '',
      date: dateString,
      isCurrent: false,
    });
    setErrors({});
    setShowCreateModal(true);
  };

  const handleOpenEditModal = async (notice: Notice) => {
    try {
      // Fetch full notice details
      const response = await getAdminNotice(notice.id);
      if (response.data) {
        const noticeData = response.data;
        // Format date for input (YYYY-MM-DD)
        let dateValue = '';
        if (noticeData.date) {
          if (noticeData.date.includes('T')) {
            dateValue = noticeData.date.split('T')[0];
          } else {
            dateValue = noticeData.date.split(' ')[0];
          }
        }
        setFormData({
          title: noticeData.title || '',
          description: noticeData.description || '',
          date: dateValue,
          isCurrent: noticeData.isCurrent || false,
        });
        setErrors({});
        setEditingNotice(noticeData);
      }
    } catch (err) {
      console.error('Error fetching notice:', err);
    }
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setEditingNotice(null);
    setFormData({
      title: '',
      description: '',
      date: '',
      isCurrent: false,
    });
    setErrors({});
  };

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required.';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Title must be at least 3 characters.';
    } else if (formData.title.trim().length > 200) {
      newErrors.title = 'Title must not exceed 200 characters.';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required.';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters.';
    } else if (formData.description.trim().length > 2000) {
      newErrors.description = 'Description must not exceed 2000 characters.';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      // Convert date to ISO string
      const dateISO = formData.date ? new Date(formData.date).toISOString() : new Date().toISOString();

      if (editingNotice) {
        console.log('Updating notice:', editingNotice.id, {
          title: formData.title.trim(),
          description: formData.description.trim(),
          date: dateISO,
          isCurrent: formData.isCurrent,
        });
        const result = await updateNotice(editingNotice.id, {
          title: formData.title.trim(),
          description: formData.description.trim(),
          date: dateISO,
          isCurrent: formData.isCurrent,
        });
        console.log('Update result:', result);
      } else {
        const result = await createNotice({
          title: formData.title.trim(),
          description: formData.description.trim(),
          date: dateISO,
          isCurrent: formData.isCurrent,
        });
        console.log('Create result:', result);
      }
      handleCloseModals();
      // Add a small delay before refetching to avoid rate limiting
      setTimeout(() => {
        fetchNotices();
      }, 500);
    } catch (err: any) {
      console.error('Error saving notice:', err);
      console.error('Error response:', err.response);
      if (err.response?.status === 429) {
        setErrors({ 
          general: 'Too many requests. Please wait a moment and try again. The server is rate-limiting requests to prevent overload.' 
        });
      } else if (err.response?.status === 400) {
        const errorData = err.response?.data;
        if (errorData?.errors) {
          setErrors(errorData.errors);
        } else {
          setErrors({ general: errorData?.message || 'Invalid data. Please check your inputs.' });
        }
      } else if (err.response?.status === 404) {
        setErrors({ general: 'Notice not found. It may have been deleted.' });
      } else if (err.response?.status === 403) {
        setErrors({ general: 'You do not have permission to perform this action.' });
      } else {
        setErrors({ general: err.response?.data?.message || err.message || 'Failed to save notice. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.noticeId) return;

    try {
      await deleteNotice(deleteModal.noticeId);
      setDeleteModal({ show: false, noticeId: null, noticeTitle: '' });
      // Add a small delay before refetching to avoid rate limiting
      setTimeout(() => {
        fetchNotices();
      }, 500);
    } catch (err: any) {
      console.error('Error deleting notice:', err);
      if (err.response?.status === 429) {
        alert('Too many requests. Please wait a moment and refresh the page.');
      } else {
        alert(err.response?.data?.message || 'Failed to delete notice. Please try again.');
      }
    }
  };

  const handleSetCurrent = async (noticeId: string) => {
    try {
      await updateNotice(noticeId, { isCurrent: true });
      // Add a small delay before refetching to avoid rate limiting
      setTimeout(() => {
        fetchNotices();
      }, 500);
    } catch (err: any) {
      console.error('Error setting current notice:', err);
      if (err.response?.status === 429) {
        alert('Too many requests. Please wait a moment and refresh the page.');
      } else {
        alert(err.response?.data?.message || 'Failed to set current notice. Please try again.');
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = async (filter: boolean | null) => {
    console.log('handleFilterChange called with filter:', filter, 'Type:', typeof filter);
    console.log('Current isCurrentFilter before change:', isCurrentFilter);
    
    // Prevent useEffect from interfering
    setIsFilterChanging(true);
    
    // Update state first
    setIsCurrentFilter(filter);
    setCurrentPage(1);
    
    console.log('State updated - isCurrentFilter set to:', filter);
    
    // Immediately fetch with new filter
    setLoading(true);
    try {
      const filters: NoticeFilters = {
        page: 1,
        limit: 10,
        isCurrent: filter,
      };
      console.log('Fetching notices immediately with filters:', filters);
      const response = await getAdminNotices(filters);
      console.log('Filter fetch response:', response);
      
      if (response && response.data) {
        if (response.data.notices) {
          let filteredNotices = Array.isArray(response.data.notices) ? response.data.notices : [];
          
          // Client-side filtering as fallback if backend doesn't filter
          if (filter !== null) {
            filteredNotices = filteredNotices.filter(notice => notice.isCurrent === filter);
            console.log('Client-side filtered notices. Count:', filteredNotices.length, 'from', response.data.notices.length);
          }
          
          setNotices(filteredNotices);
          setTotalPages(response.data.totalPages || 1);
          setTotal(filteredNotices.length);
          console.log('Notices updated. Count:', filteredNotices.length);
        }
      }
    } catch (err: any) {
      console.error('Error fetching filtered notices:', err);
      console.error('Error response:', err.response);
    } finally {
      setLoading(false);
      // Allow useEffect to work again after a short delay
      setTimeout(() => {
        setIsFilterChanging(false);
      }, 100);
    }
  };

  if (loading && notices.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      <div className="mb-3">
        <Link to="/dashboard" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Dashboard
        </Link>
      </div>
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 gap-3">
        <div>
          <h1 className="h3 section-title">NOTICE MANAGEMENT</h1>
          <p className="text-muted-soft mb-0">Create, edit, and manage notices</p>
        </div>
        <div className="create-notice-btn-container" style={{ flexShrink: 0, minWidth: 'fit-content', display: 'flex' }}>
          <button 
            className="btn btn-arena create-notice-btn" 
            onClick={handleOpenCreateModal}
            style={{ 
              display: 'inline-flex',
              visibility: 'visible',
              opacity: 1,
              position: 'relative',
              zIndex: 10,
              minWidth: '150px'
            }}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Create Notice
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card card-surface mb-4">
        <div className="card-body">
          <div className="d-flex flex-column flex-md-row flex-wrap gap-2 align-items-start align-items-md-center">
            <span className="fw-semibold mb-2 mb-md-0">Filter by Status:</span>
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className={`btn btn-sm ${isCurrentFilter === null ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={(e) => {
                  e.preventDefault();
                  console.log('All button clicked');
                  handleFilterChange(null);
                }}
              >
                All
              </button>
              <button
                type="button"
                className={`btn btn-sm ${isCurrentFilter === true ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={(e) => {
                  e.preventDefault();
                  console.log('Current button clicked');
                  handleFilterChange(true);
                }}
              >
                Current
              </button>
              <button
                type="button"
                className={`btn btn-sm ${isCurrentFilter === false ? 'btn-arena' : 'btn-outline-secondary'}`}
                onClick={(e) => {
                  e.preventDefault();
                  console.log('Archived button clicked');
                  handleFilterChange(false);
                }}
              >
                Archived
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notices List */}
      <div className="card card-surface">
        <div className="card-body">
          {notices.length === 0 ? (
            <div className="alert alert-secondary mb-0">No notices found.</div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notices.map((notice) => (
                      <tr key={notice.id}>
                        <td className="fw-semibold">{notice.title}</td>
                        <td>
                          <span className="text-muted-soft">
                            {notice.description.length > 100
                              ? `${notice.description.substring(0, 100)}...`
                              : notice.description}
                          </span>
                        </td>
                        <td>{new Date(notice.date).toLocaleDateString()}</td>
                        <td>
                          <span className={`badge ${notice.isCurrent ? 'bg-success' : 'bg-secondary'} badge-status`}>
                            {notice.isCurrent ? 'Current' : 'Archived'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            {!notice.isCurrent && (
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={() => handleSetCurrent(notice.id)}
                                title="Set as Current"
                              >
                                <i className="bi bi-star"></i>
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleOpenEditModal(notice)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                setDeleteModal({
                                  show: true,
                                  noticeId: notice.id,
                                  noticeTitle: notice.title,
                                })
                              }
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="d-md-none">
                {notices.map((notice) => (
                  <div key={notice.id} className="card card-surface mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="mb-1 fw-bold">{notice.title}</h6>
                          <p className="text-muted-soft small mb-1">{notice.description}</p>
                          <div className="small text-muted-soft mb-2">
                            <i className="bi bi-calendar me-1"></i>
                            {new Date(notice.date).toLocaleDateString()}
                          </div>
                        </div>
                        <span className={`badge ${notice.isCurrent ? 'bg-success' : 'bg-secondary'} badge-status`}>
                          {notice.isCurrent ? 'Current' : 'Archived'}
                        </span>
                      </div>
                      <div className="d-grid gap-2">
                        {!notice.isCurrent && (
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={() => handleSetCurrent(notice.id)}
                          >
                            <i className="bi bi-star me-1"></i>
                            Set as Current
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleOpenEditModal(notice)}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() =>
                            setDeleteModal({
                              show: true,
                              noticeId: notice.id,
                              noticeTitle: notice.title,
                            })
                          }
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <nav className="mt-4" aria-label="Notices pagination">
                  <ul className="pagination justify-content-center flex-wrap gap-1">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    {Array.from({ length: Math.min(totalPages, 10) }, (_, index) => {
                      const page = index + 1;
                      return (
                        <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                          <button type="button" className="page-link" onClick={() => handlePageChange(page)}>
                            {page}
                          </button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                  <div className="text-center mt-2 text-muted-soft small">
                    Showing {notices.length} of {total} notice{total !== 1 ? 's' : ''}
                  </div>
                </nav>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingNotice) && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={handleCloseModals}
            style={{ zIndex: 1040 }}
          ></div>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1050, position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', overflow: 'auto' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" style={{ margin: '1rem', maxWidth: '600px' }}>
              <div className="modal-content card-surface" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
                <div className="modal-header border-bottom">
                  <h5 className="modal-title">{editingNotice ? 'Edit Notice' : 'Create Notice'}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModals}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                  <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                    {errors.general && (
                      <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        {errors.general}
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => {
                            const newErrors = { ...errors };
                            delete newErrors.general;
                            setErrors(newErrors);
                          }}
                          aria-label="Close"
                        ></button>
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">
                        Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                        value={formData.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        disabled={saving}
                        maxLength={200}
                        placeholder="Enter notice title"
                      />
                      {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                      <div className="form-text">3-200 characters</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Description <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                        rows={5}
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        disabled={saving}
                        maxLength={2000}
                        placeholder="Enter notice description"
                      />
                      {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                      <div className="form-text">10-2000 characters</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                        value={formData.date}
                        onChange={(e) => handleChange('date', e.target.value)}
                        disabled={saving}
                      />
                      {errors.date && <div className="invalid-feedback">{errors.date}</div>}
                    </div>

                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="isCurrent"
                          checked={formData.isCurrent}
                          onChange={(e) => handleChange('isCurrent', e.target.checked)}
                          disabled={saving}
                        />
                        <label className="form-check-label" htmlFor="isCurrent">
                          Set as current notice (will replace any existing current notice)
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer border-top d-flex flex-column flex-sm-row gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary modal-footer-btn"
                      onClick={handleCloseModals}
                      disabled={saving}
                      style={{
                        display: 'inline-flex',
                        visibility: 'visible',
                        opacity: 1,
                        minWidth: '100px'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-arena modal-footer-btn" 
                      disabled={saving}
                      style={{
                        display: 'inline-flex',
                        visibility: 'visible',
                        opacity: 1,
                        minWidth: '150px'
                      }}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        editingNotice ? 'Update Notice' : 'Create Notice'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      <DeleteNoticeModal
        show={deleteModal.show}
        noticeTitle={deleteModal.noticeTitle}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ show: false, noticeId: null, noticeTitle: '' })}
      />
    </div>
  );
};

export default AdminNoticesPage;
